<template>
  <span>
    <!-- Botão trigger -->
    <v-btn
      small
      rounded
      :color="triggerColor"
      :dark="triggerAge !== 12"
      :light="triggerAge === 12"
      @click="openPicker"
      class="age-rating-trigger-btn"
    >
      <v-icon left small>mdi-shield-account</v-icon>
      {{ triggerLabel }}
      <v-icon right x-small class="ml-1">mdi-pencil</v-icon>
    </v-btn>

    <v-dialog v-model="open" max-width="480" scrollable>
      <v-card dark color="#1e1e1e">
        <div style="padding: 20px 20px 14px; display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;">
          <div>
            <div style="font-size: 16px; font-weight: 500; color: rgba(255,255,255,0.87); margin-bottom: 6px;">
              Descritores de Conteúdo
            </div>
            <div style="font-size: 12px; line-height: 1.6; color: rgba(255,255,255,0.5);">
              Marque os elementos de conteúdo presentes nesta obra.<br>
              A classificação mais alta será aplicada automaticamente.
            </div>
          </div>
          <div style="flex-shrink: 0; padding-top: 2px;">
            <v-chip
              v-if="resultRating"
              :color="ratingColor(resultRating.age)"
              :dark="resultRating.age !== 12"
              :light="resultRating.age === 12"
              small
              label
              class="font-weight-bold"
            >
              {{ resultRating.rating }}
            </v-chip>
            <v-chip v-else color="green darken-1" dark small label>Livre</v-chip>
          </div>
        </div>

        <v-divider></v-divider>

        <v-card-text class="pa-0" style="max-height: 420px; overflow-y: auto;">
          <!-- Opção Livre -->
          <div
            class="age-picker-item"
            :class="{ 'age-picker-item--active': freeSelected }"
            @click="selectFree"
          >
            <v-icon
              class="age-picker-item__check mr-3"
              :color="freeSelected ? 'var(--zinnes-primary)' : 'grey darken-1'"
              small
            >
              {{ freeSelected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
            </v-icon>
            <span class="age-picker-item__desc">Livre para todos os públicos</span>
            <v-chip x-small color="green darken-1" dark label class="age-picker-item__badge ml-auto">
              Livre
            </v-chip>
          </div>

          <v-divider style="border-color: rgba(255,255,255,0.08);"></v-divider>

          <div
            v-for="item in ageRatings"
            :key="item.id"
            class="age-picker-item"
            :class="{ 'age-picker-item--active': isSelected(item.id) }"
            @click="toggleItem(item.id)"
          >
            <v-icon
              class="age-picker-item__check mr-3"
              :color="isSelected(item.id) ? 'var(--zinnes-primary)' : 'grey darken-1'"
              small
            >
              {{ isSelected(item.id) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
            </v-icon>

            <span class="age-picker-item__desc">{{ item.description }}</span>

            <v-chip
              x-small
              :color="ratingColor(item.age)"
              :dark="item.age !== 12"
              :light="item.age === 12"
              label
              class="age-picker-item__badge ml-auto"
            >
              {{ item.rating }}
            </v-chip>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <div style="padding: 10px 16px; display: flex; align-items: center; gap: 6px;">
          <v-icon small color="red lighten-1">mdi-alert-circle-outline</v-icon>
          <span style="font-size: 11px; color: rgba(255,255,255,0.45); line-height: 1.4;">
            Conteúdo +18 não é permitido na plataforma.
          </span>
        </div>

        <v-divider></v-divider>

        <v-card-actions class="pa-3">
          <v-btn text color="grey lighten-1" small @click="cancel">Cancelar</v-btn>
          <v-spacer></v-spacer>
          <v-btn class="zinnes-primary-btn" dark small @click="confirm">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import { getHighestAgeRating, normalizeAgeRatingIds } from '@/utils/ageRatings';

export default {
  name: 'AgeRatingPickerModal',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: [Array, Number, String, Object],
      default: () => [],
    },
    ageRatings: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      open: false,
      selectedIds: [],
    };
  },
  computed: {
    freeSelected() {
      return this.selectedIds.length === 0;
    },
    resultRating() {
      return getHighestAgeRating(this.ageRatings, this.selectedIds);
    },
    triggerRating() {
      return getHighestAgeRating(this.ageRatings, normalizeAgeRatingIds(this.value));
    },
    triggerLabel() {
      return this.triggerRating ? this.triggerRating.rating : '-- Livre --';
    },
    triggerAge() {
      return this.triggerRating ? this.triggerRating.age : 0;
    },
    triggerColor() {
      if (!this.triggerRating) return 'green darken-1';
      return this.ratingColor(this.triggerRating.age);
    },
  },
  methods: {
    openPicker() {
      this.selectedIds = normalizeAgeRatingIds(this.value);
      this.open = true;
    },
    selectFree() {
      this.selectedIds = [];
    },
    toggleItem(id) {
      const normalizedId = normalizeAgeRatingIds(id)[0];
      if (normalizedId === undefined) return;

      const nextSelectedIds = [...this.selectedIds];
      const idx = nextSelectedIds.indexOf(normalizedId);
      if (idx === -1) {
        nextSelectedIds.push(normalizedId);
      } else {
        nextSelectedIds.splice(idx, 1);
      }
      this.selectedIds = nextSelectedIds;
    },
    confirm() {
      this.$emit('input', [...this.selectedIds]);
      this.open = false;
    },
    cancel() {
      this.open = false;
    },
    isSelected(id) {
      const normalizedId = normalizeAgeRatingIds(id)[0];
      return normalizedId !== undefined && this.selectedIds.includes(normalizedId);
    },
    ratingColor(age) {
      if (age <= 10) return '#0095DA';
      if (age <= 12) return '#FBC115';
      if (age <= 14) return '#F58220';
      return '#EC1D25';
    },
  },
};
</script>

<style scoped>
.age-rating-trigger-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}

.age-picker-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  transition: background 0.15s;
  user-select: none;
}

.age-picker-item:last-child {
  border-bottom: none;
}

.age-picker-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.age-picker-item--active {
  background: rgba(107, 33, 168, 0.15);
}

.age-picker-item--active:hover {
  background: rgba(107, 33, 168, 0.22);
}

.age-picker-item__check {
  flex-shrink: 0;
}

.age-picker-item__desc {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.87);
  flex: 1;
}

.age-picker-item__badge {
  font-size: 10px !important;
  flex-shrink: 0;
  font-weight: 700 !important;
}
</style>
